import {Component, computed, inject, Input, signal} from '@angular/core';
import {MatCard, MatCardContent, MatCardHeader, MatCardTitle} from "@angular/material/card";
import {IGameQueryResult} from "../../interfaces/IGameQueryResult";
import {IActiveGameInstance} from "../../interfaces/IActiveGameInstance";
import {PipesModule} from "../../pipes/pipes.module";
import {GameStateEnum} from "../../enum/GameStateEnum";
import {HasPermissionDirective} from "../../directives/permissions/has-permission";
import {MatButton} from "@angular/material/button";
import {MatIcon} from "@angular/material/icon";
import {MatTooltip} from "@angular/material/tooltip";
import {PermissionTypes} from "../../enum/PermissionTypes";
import {DatePipe} from "@angular/common";
import {MatProgressSpinner} from "@angular/material/progress-spinner";
import {GameService} from "../../services/game.service";
import {SnackbarService} from "../../services/snackbar.service";
import {UIStateEnum} from "../../enum/UIStateEnum";
import {ActiveUserService} from "../../services/active-user.service";
import {IForceDrawWinner} from "../../interfaces/IForceDrawWinner";
import {GameTypeEnum} from "../../enum/GameTypeEnum";
import {MatSlideToggle} from "@angular/material/slide-toggle";
import {MatDialog, MatDialogConfig} from '@angular/material/dialog';
import {concatMap, of} from 'rxjs';
import {PauseGameDialogComponent} from '../dialogs/pause-game-dialog/pause-game-dialog.component';
import {StopGameDialogComponent} from '../dialogs/stop-game-dialog/stop-game-dialog.component';
import {EndGameDialogComponent} from '../dialogs/end-game-dialog/end-game-dialog.component';
import {
  ChangeUnpauseDateDialogComponent
} from '../dialogs/change-unpause-date-dialog/change-unpause-date-dialog.component';
import {ComponentType} from "@angular/cdk/overlay";

@Component({
  selector: 'app-game-state-settings',
  standalone: true,
  imports: [
    MatCardHeader,
    MatCard,
    MatCardTitle,
    MatCardContent,
    PipesModule,
    HasPermissionDirective,
    MatButton,
    MatIcon,
    MatTooltip,
    DatePipe,
    MatProgressSpinner,
    MatSlideToggle
  ],
  templateUrl: './game-state-settings.component.html',
  styleUrl: './game-state-settings.component.css'
})
export class GameStateSettingsComponent {
  @Input() set activeGame(value: IGameQueryResult | null) {
    this._activeGame.set(value);
  }

  get activeGame(): IGameQueryResult | null {
    return this._activeGame();
  }

  @Input() activeDraw: IActiveGameInstance | null = null;

  private readonly _activeGame = signal<IGameQueryResult | null>(null);

  private gameService: GameService = inject(GameService);
  private activeUserService: ActiveUserService = inject(ActiveUserService);
  private snackbarService: SnackbarService = inject(SnackbarService);
  private matDialog: MatDialog = inject(MatDialog);

  public uiState = UIStateEnum.ShowData;
  public uiStateEnum = UIStateEnum;
  protected readonly gameStateEnum = GameStateEnum;
  protected readonly gameTypeEnum = GameTypeEnum;
  protected readonly permissionTypes = PermissionTypes;

  public activeUser = this.activeUserService.activeUser();

  protected readonly isGamePausedOrPendingPause = computed(() => {
    const game = this._activeGame();
    if (!game) return false;

    const pauseStates = [
      GameStateEnum.Paused,
      GameStateEnum.PauseAfterNextDraw,
      GameStateEnum.PauseAfterNextWinner,
      GameStateEnum.PauseAfterNextDrawCancelAutoplays,
      GameStateEnum.PauseAfterNextWinnerCancelAutoplays
    ];

    return pauseStates.includes(game.State);
  });

  openPauseGame() {
    this.openDialogAndRefreshGame(PauseGameDialogComponent, {
      game: this.activeGame,
      draw: this.activeDraw
    });
  }

  openStopGame() {
    this.openDialogAndRefreshGame(StopGameDialogComponent, this.activeGame);
  }

  openEndGame() {
    this.openDialogAndRefreshGame(EndGameDialogComponent, {
      game: this.activeGame,
      draw: this.activeDraw
    });
  }

  openChangeUnpauseDate() {
    this.openDialogAndRefreshGame(ChangeUnpauseDateDialogComponent, {
      game: this.activeGame
    });
  }

  updateGameShouldForceWinnerStatus() {
    this.uiState = UIStateEnum.ShowLoading;

    const command: IForceDrawWinner = {
      AdminId: this.activeUser.Id,
      GameId: this.activeGame!.Id
    };

    this.gameService.updateGameForceWinner(command).subscribe({
      next: () => {
        this.activeDraw!.ForceWinner = !this.activeDraw!.ForceWinner;
        this.uiState = UIStateEnum.ShowData;
        this.snackbarService.openSuccessfulSnackBar(`Update successful. The current draw for ${this.activeGame?.Name} will force a winner.`);
      },
      error: () => {
        this.uiState = UIStateEnum.ShowData;
        this.snackbarService.openErrorSnackBar("Failed to update the force winner status.");
      }
    });
  }

  private openDialogAndRefreshGame<T>(component: ComponentType<T>, data: unknown) {
    const config: MatDialogConfig = {
      data,
      width: '650px',
    };

    this.matDialog.open(component, config)
      .afterClosed()
      .pipe(concatMap((res) => {
        if (res && this.activeGame) {
          return this.gameService.getGameByGameId(this.activeGame.Id);
        }
        return of(null);
      }))
      .subscribe({
        next: (res: IGameQueryResult | null) => {
          if (res) {
            this._activeGame.set(res);
          }
        },
        error: () => {
          this.snackbarService.openErrorSnackBar('Looks like there was an error retrieving your game info. Please try again or contact a system admin.');
        }
      });
  }
}
